import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ems/ui/components/card'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { QUOTA_KEYS } from '@ems/contracts/plan-quotas'
import { statusLabel, statusTone } from '#admin/features/platform/tenants/status.ts'
import { quotaState, usageQuery } from '#admin/features/platform/usage/api.ts'
import { QuotaMeters } from '#admin/features/platform/usage/quota-meter.tsx'
import { UsageSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/**
 * `/platform/usage` — every edition against its plan's quotas, the fullest first, so the edition
 * about to hit a ceiling is at the top rather than wherever its slug sorts.
 */
export const Route = createFileRoute('/_authenticated/platform/usage')({
  pendingComponent: UsageSkeleton,
  staticData: { crumb: 'Usage' },
  loader: ({ context }) => context.queryClient.ensureQueryData(usageQuery()),
  component: UsagePage,
})

function fullness(row: Parameters<typeof QuotaMeters>[0]['row']): number {
  return Math.max(
    0,
    ...QUOTA_KEYS.map((q) => {
      const state = quotaState(row.usage[q] ?? null, row.quotas[q])
      return state.tone === 'unknown' || state.tone === 'unlimited' ? 0 : state.ratio
    }),
  )
}

function UsagePage() {
  const { data } = useSuspenseQuery(usageQuery())
  const rows = data.items.toSorted((a, b) => fullness(b) - fullness(a))

  return (
    <>
      <PageHeader
        title="Usage"
        description="Counted live against each plan's quotas. Amber from 80%, red at the ceiling."
      />
      <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <li key={row.tenantId}>
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle>
                    <Link
                      to="/platform/tenants/$tenantId/usage"
                      params={{ tenantId: row.tenantId }}
                      className="hover:underline"
                    >
                      {row.tenantName}
                    </Link>
                  </CardTitle>
                  {row.status === 'active' ? null : (
                    <StatusBadge tone={statusTone(row.status)}>
                      {statusLabel(row.status)}
                    </StatusBadge>
                  )}
                </div>
                <CardDescription>
                  <span className="font-mono">{row.tenantSlug}</span> · {row.planName} plan
                </CardDescription>
              </CardHeader>
              <CardContent>
                <QuotaMeters row={row} />
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </>
  )
}
